import type { I18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { defineFillAction } from '@/shared/form/agent-fill-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { chatbotFilters } from './chatbot.filters';
import { CHATBOT_TABS } from './chatbot.search';

// Manifestos do Chatbot (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K. O legado não tem
// permissão de recurso: só o tenant raiz (`isRootTenancy`, router.tsx:750-754); o relatório também pede
// `FEATURE_FLAGS.SHOW_REFUSAL_IN_CHATBOT` (router.tsx:757-763).

export const CHATBOT_PATH = '/chatbot';
export const CHATBOT_REPORT_PATH = '/chatbot/relatorios';
export const NEW_CHATBOT = 'novo';
export const chatbotPath = (id: string) => `${CHATBOT_PATH}/${encodeURIComponent(id)}`;

const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela do Chatbot para usar esta ação.`);
};
const rowInput = z.strictObject({ id: z.string().min(1).max(100) });
export type ChatbotRowInput = z.infer<typeof rowInput>;

/** Textos do modal do legado (ChatbotListContainer.tsx:115-129, ChatbotFormContainer.tsx:266-282). */
export function deletePreviewText() {
  return { title: t`Excluir`, detail: t`Tem certeza que deseja excluir esse registro?`, tone: 'destructive' as const };
}

// --- Lista -----------------------------------------------------------------------------------------------

export const chatbotCreate = action({
  id: 'chatbot.create',
  kind: 'navigate',
  label: msg`Novo registro de entrada`,
  description: msg`Abre o cadastro de um novo registro de entrada do chatbot, em branco.`,
  keywords: [msg`novo`, msg`chatbot`],
  run: ({ nav }) => nav.to(chatbotPath(NEW_CHATBOT)),
});

export const chatbotOpenReport = action({
  id: 'chatbot.report.open',
  kind: 'navigate',
  label: msg`Relatórios`,
  description: msg`Abre o relatório de recusas do chatbot.`,
  run: ({ nav }) => nav.to(CHATBOT_REPORT_PATH),
});

export const chatbotListRefresh = action({
  id: 'chatbot.list.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de registros de entrada do chatbot.`,
  run: mountedScreenRequired,
});

export const chatbotFilter = filterAction('chatbot.filter', chatbotFilters, {
  description: msg`Filtra a lista de registros de entrada: valor em reais (1.000,00), número de telefone, nome do produto, categoria (FGTS ou CDC), nome do tomador, nome do beneficiário, nome da fila e código de identificação. Filtro omitido é limpo.`,
});

export const chatbotListPage = listPageAction('chatbot.list.page');

export const chatbotOpen = action<ChatbotRowInput>({
  id: 'chatbot.open',
  kind: 'navigate',
  row: true,
  label: msg`Editar`,
  description: msg`Abre o cadastro de um registro de entrada da lista.`,
  input: rowInput,
  run: ({ nav, input }) => nav.to(chatbotPath(input.id)),
});

export const chatbotCopyCode = action<ChatbotRowInput>({
  id: 'chatbot.copyCode',
  kind: 'read',
  row: true,
  label: msg`Copiar código de identificação`,
  description: msg`Copia para a área de transferência o código de identificação de um registro da página atual.`,
  input: rowInput,
  run: mountedScreenRequired,
});

export const chatbotContact = action<ChatbotRowInput>({
  id: 'chatbot.contact',
  kind: 'draft',
  row: true,
  label: msg`Entrar em contato`,
  description: msg`Abre o painel "Entre em contato via WhatsApp" de um registro da página atual, com o QR Code e o link da conversa. Registro já utilizado não tem essa opção.`,
  input: rowInput,
  run: mountedScreenRequired,
});

export const chatbotListDelete = action<ChatbotRowInput>({
  id: 'chatbot.list.delete',
  kind: 'destructive',
  row: true,
  label: msg`Excluir`,
  description: msg`Exclui um registro de entrada da página atual, depois da confirmação da pessoa. Registro já utilizado não pode ser excluído.`,
  input: rowInput,
  preview: () => deletePreviewText(),
  run: mountedScreenRequired,
});

export const chatbotListScreen = defineScreen({
  id: 'chatbot.list',
  path: CHATBOT_PATH,
  title: msg`Chatbot`,
  description: msg`Registros de entrada do chatbot do WhatsApp: produto, empresa, fila, telefone, tomador, beneficiário e valor, com filtros e paginação; dá para abrir, excluir, copiar o código e gerar o link de contato.`,
  access: { rootTenancy: true },
  place: { icon: 'bot', section: 'chatbot' },
  actions: [chatbotOpenReport, chatbotListRefresh, chatbotCreate, chatbotFilter, chatbotListPage, chatbotOpen, chatbotContact, chatbotCopyCode, chatbotListDelete],
});

export function chatbotListContext(input: { readonly total: number | undefined; readonly filters: readonly string[] }, i18n: I18n): ScreenContext {
  const total = input.total;
  const active = input.filters.join(', ');
  const state = total === undefined ? i18n._(msg`Carregando.`) : i18n._(msg`Registros encontrados: ${total}.`);
  const filters = input.filters.length > 0 ? i18n._(msg`Filtros: ${active}.`) : i18n._(msg`Sem filtros.`);
  return {
    summary: `${i18n._(msg`Registros de entrada do chatbot.`)} ${state} ${filters}`,
    fields: { total: total ?? null },
    sensitivity: 'internal',
  };
}

// --- Relatório -------------------------------------------------------------------------------------------

export const REPORT_PRESETS = ['today', 'yesterday', 'week', '1m', '3m', '1y'] as const;
export type ReportPreset = (typeof REPORT_PRESETS)[number];
const civilDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const chatbotReportPeriodInput = z
  .strictObject({ preset: z.enum(REPORT_PRESETS).optional(), start: civilDate.optional(), end: civilDate.optional() })
  .refine((value) => (value.preset === undefined ? value.start !== undefined && value.end !== undefined : value.start === undefined && value.end === undefined), {
    message: 'use preset, or start and end',
  })
  .refine((value) => value.start === undefined || value.end === undefined || value.start <= value.end, { message: 'start must not be after end' });
export type ChatbotReportPeriodInput = z.infer<typeof chatbotReportPeriodInput>;

export const chatbotReportPeriod = action<ChatbotReportPeriodInput>({
  id: 'chatbot.report.period',
  kind: 'read',
  label: msg`Escolher período`,
  description: msg`Troca o período do relatório de recusas. Use um botão (today = Hoje, yesterday = Ontem, week = semana atual, 1m, 3m ou 1y = 1 ano) ou um intervalo de datas (AAAA-MM-DD).`,
  input: chatbotReportPeriodInput,
  run: mountedScreenRequired,
});

export const chatbotReportScreen = defineScreen({
  id: 'chatbot.report',
  path: CHATBOT_REPORT_PATH,
  title: msg`Relatório`,
  description: msg`Relatório de recusas do chatbot no período: quantas consultas foram recusadas e quantas vezes apareceu cada motivo.`,
  access: { rootTenancy: true, flag: { key: 'FEATURE_FLAGS.SHOW_REFUSAL_IN_CHATBOT', equals: true } },
  place: { icon: 'receipt-text', parent: 'chatbot.list' },
  actions: [chatbotReportPeriod],
});

export function chatbotReportContext(input: { readonly period: string; readonly refusals: number | null | undefined; readonly reasons: number; readonly loading: boolean }, i18n: I18n): ScreenContext {
  const period = input.period;
  const refusals = input.refusals ?? 0;
  const reasons = input.reasons;
  const numbers = input.loading ? i18n._(msg`Carregando.`) : i18n._(msg`Recusas: ${refusals}, em ${reasons} motivos.`);
  return {
    summary: `${i18n._(msg`Relatório de recusas do chatbot.`)} ${i18n._(msg`Período: ${period}.`)} ${numbers}`,
    fields: { refusals: input.refusals ?? null, reasons: input.reasons },
    sensitivity: 'internal',
  };
}

// --- Registro --------------------------------------------------------------------------------------------

export const chatbotSave = action({
  id: 'chatbot.save',
  kind: 'write',
  label: msg`Salvar`,
  description: msg`Salva o registro de entrada aberto com os dados que estão no formulário agora. A pessoa confirma antes.`,
  input: z.strictObject({}),
  preview: () => ({ title: t`Salvar`, detail: t`Os dados de entrada que estão no formulário serão salvos.` }),
  run: mountedScreenRequired,
});

export const chatbotDetailRefresh = action({
  id: 'chatbot.detail.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo o registro de entrada aberto.`,
  run: mountedScreenRequired,
});

export const chatbotDetailContact = action({
  id: 'chatbot.detail.contact',
  kind: 'draft',
  label: msg`Entrar em contato`,
  description: msg`Abre o painel "Entre em contato via WhatsApp" do registro aberto, com o QR Code e o link da conversa.`,
  run: mountedScreenRequired,
});

export const chatbotDelete = action({
  id: 'chatbot.delete',
  kind: 'destructive',
  label: msg`Excluir`,
  description: msg`Exclui o registro de entrada aberto, depois da confirmação da pessoa, e volta para a lista.`,
  input: z.strictObject({}),
  preview: () => deletePreviewText(),
  run: mountedScreenRequired,
});

/** O Luy prepara o rascunho do cadastro (U56): só campos de texto, valor e "padrão"; Salvar continua com a pessoa. */
export const chatbotFill = defineFillAction({
  id: 'chatbot.fill',
  description: msg`Preenche, como rascunho, campos do cadastro aberto: telefone do chatbot (só dígitos, com DDD), nome do parceiro, origem da campanha, valor (em centavos), mensagem e se é o registro padrão. Não troca campo que a pessoa já editou. Quem salva é a pessoa, no botão Salvar.`,
  fields: {
    botPhone: { schema: z.string().regex(/^\d{10,11}$/), label: msg`Telefone do chatbot` },
    companyName: { schema: z.string().trim().min(1).max(200), label: msg`Nome do parceiro` },
    originCampaign: { schema: z.string().trim().min(1).max(200), label: msg`Origem da campanha` },
    invoiceValue: { schema: z.number().int().min(0), label: msg`Valor` },
    incomingMessage: { schema: z.string().trim().min(1).max(1000), label: msg`Escreva uma mensagem` },
    isDefault: { schema: z.boolean(), label: msg`Definir como padrão` },
  },
  mountedMessage: msg`Abra o cadastro do registro de entrada para o Luy preencher.`,
});

export const CHATBOT_TAB_LABELS = { informacoes: msg`Informações`, atendimento: msg`Atendimento` } as const;

export const chatbotDetailScreen = defineScreen({
  id: 'chatbot.detail',
  path: `${CHATBOT_PATH}/$id`,
  title: msg`Dados de entrada`,
  description: msg`Cadastro de um registro de entrada do chatbot: telefone, parceiro, campanha, fila, produto, valor, tomador, beneficiário e mensagem, e a aba Atendimento com o acompanhamento da conversa.`,
  // Aninhada em `/chatbot` no legado (router.tsx:747-775): herda o tenant raiz da rota-mãe.
  access: { rootTenancy: true },
  place: {
    icon: 'bot',
    parent: 'chatbot.list',
    record: msg`Dados de entrada`,
    tabs: CHATBOT_TABS.map((id) => ({ id, label: CHATBOT_TAB_LABELS[id] })),
  },
  actions: [chatbotSave, chatbotDetailRefresh, chatbotDetailContact, chatbotDelete, chatbotFill],
});

export function chatbotDetailContext(
  input: { readonly mode: 'create' | 'edit'; readonly tab: string; readonly used: boolean; readonly dirty: boolean; readonly code: string | null | undefined },
  i18n: I18n,
): ScreenContext {
  const code = input.code ?? '';
  const head = input.mode === 'create' ? i18n._(msg`Novo registro de entrada do chatbot, em branco.`) : i18n._(msg`Registro de entrada ${code} do chatbot.`);
  const tab = input.tab === 'atendimento' ? i18n._(msg`Aba Atendimento.`) : i18n._(msg`Aba Informações.`);
  const used = input.used ? i18n._(msg`Já utilizado: não pode ser alterado.`) : '';
  const dirty = input.dirty ? i18n._(msg`Com alterações não salvas.`) : '';
  return {
    summary: [head, input.mode === 'edit' ? tab : '', used, dirty].filter((part) => part !== '').join(' '),
    fields: { mode: input.mode, tab: input.tab, used: input.used, dirty: input.dirty },
    sensitivity: 'restricted',
  };
}
